import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Car, Search, PackageCheck, Quote, Briefcase, Star, Wrench } from "lucide-react";
import { Reveal, Stagger, Item } from "../fx/Reveal";
import { useSettings } from "../../context/SettingsContext";
import api from "../../services/api";

const BRANDS = ["BOSCH", "VALEO", "BREMBO", "MANN-FILTER", "SKF", "NGK", "CONTINENTAL", "SACHS", "FEBI", "TRW", "GATES", "MAHLE"];

export const BrandMarquee = () => (
  <section data-testid="brand-marquee" className="relative border-y border-white/5 bg-[#0B0D12] py-5 overflow-hidden">
    <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#0B0D12] to-transparent z-10 pointer-events-none" />
    <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#0B0D12] to-transparent z-10 pointer-events-none" />
    <div className="marquee-track-slow flex items-center gap-14 w-max">
      {[...BRANDS, ...BRANDS].map((b, i) => (
        <span key={i} className="font-display font-extrabold italic text-2xl md:text-3xl tracking-tighter text-chrome opacity-70 hover:opacity-100 transition-opacity whitespace-nowrap flex items-center gap-14">
          {b} <span className="w-1.5 h-1.5 rounded-full bg-[#E01F1F] shrink-0" />
        </span>
      ))}
    </div>
  </section>
);

const useCounter = (target, duration = 1400) => {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const tick = (t) => {
        const k = Math.min(1, (t - t0) / duration);
        setV(Math.round(target * (1 - Math.pow(1 - k, 3))));
        if (k < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);
  return [v, ref];
};

const Stat = ({ value, suffix, label }) => {
  const [v, ref] = useCounter(value);
  return (
    <div ref={ref} className="flex flex-col items-start">
      <div className="font-display font-extrabold italic text-4xl md:text-5xl text-white tracking-tight leading-none">
        {v.toLocaleString("fr-FR")}<span className="text-[#FF3B3B]">{suffix}</span>
      </div>
      <div className="text-[12px] uppercase tracking-[0.2em] text-slate-500 mt-2">{label}</div>
    </div>
  );
};

export const StatsStrip = () => {
  const { delivery_delay, return_days } = useSettings();
  const hours = parseInt(String(delivery_delay).match(/(\d+)h?\s*$/)?.[1] || String(delivery_delay).match(/\d+/)?.[0] || 48, 10);
  return (
    <div data-testid="home-stats" className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 max-w-[1040px] mx-auto mt-12 px-2">
      <Stat value={650000} suffix="+" label="Références" />
      <Stat value={hours} suffix="h" label="Livraison max" />
      <Stat value={98} suffix="%" label="Clients satisfaits" />
      <Stat value={return_days} suffix="j" label="Retours gratuits" />
    </div>
  );
};

const stepsFor = (s) => [
  { n: "01", icon: Car, title: "Identifiez votre véhicule", text: "Plaque d'immatriculation ou marque / modèle : nous reconnaissons votre voiture en une seconde." },
  { n: "02", icon: Search, title: "Choisissez la bonne pièce", text: "Plus de 650 000 références, uniquement des pièces 100 % compatibles, triées par marque, qualité et prix." },
  { n: "03", icon: PackageCheck, title: `Recevez-la en ${s.delivery_delay}`, text: `Expédition express, suivi en temps réel et retours gratuits pendant ${s.return_days} jours.` },
];

export const HowItWorks = () => {
  const settings = useSettings();
  const STEPS = stepsFor(settings);
  return (
  <section className="relative max-w-[1440px] mx-auto px-5 mt-24 overflow-hidden">
    <div className="ghost-text absolute -top-6 -left-2 select-none pointer-events-none">KTS</div>
    <Reveal>
      <div className="flex items-end justify-between mb-10 relative">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B3B] font-bold mb-3">Simple. Rapide. Précis.</div>
          <h2 className="speed-line font-extrabold italic uppercase tracking-tight text-slate-50 text-3xl md:text-4xl">Comment ça marche</h2>
        </div>
      </div>
    </Reveal>
    <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5 relative">
      {STEPS.map((s, i) => (
        <Item key={s.n} className="group relative bg-[#10131A] border border-[#1E232D] hover:border-[#E01F1F]/50 rounded-2xl p-7 overflow-hidden transition-colors">
          <div className="absolute -right-3 -top-6 font-display font-black italic text-[120px] leading-none text-white/[0.035] group-hover:text-[#E01F1F]/10 transition-colors select-none">{s.n}</div>
          <div className="w-12 h-12 rounded-xl bg-[#08090C] border border-[#2A3040] flex items-center justify-center mb-6 group-hover:border-[#E01F1F]/60 transition-colors">
            <s.icon size={22} className="text-[#FF3B3B]" />
          </div>
          <div className="text-[11px] font-bold tracking-[0.25em] text-slate-500 mb-2">ÉTAPE {s.n}</div>
          <h3 className="font-display font-bold text-xl text-white mb-2">{s.title}</h3>
          <p className="text-slate-400 text-[14px] leading-relaxed">{s.text}</p>
          {i < STEPS.length - 1 && <div className="hidden md:block absolute right-0 top-1/2 w-5 h-px bg-gradient-to-r from-[#E01F1F] to-transparent translate-x-full" />}
        </Item>
      ))}
    </Stagger>
  </section>
  );
};

export const ProCta = () => (
  <section className="max-w-[1440px] mx-auto px-5 mt-24">
    <Reveal>
      <div data-testid="home-pro-cta" className="relative rounded-3xl overflow-hidden border border-emerald-900/50 bg-[#0A0F0C] grid grid-cols-1 lg:grid-cols-2">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_0%_50%,rgba(34,197,94,0.18),transparent_55%)] pointer-events-none" />
        <div className="relative z-10 p-9 md:p-14">
          <span className="inline-flex items-center gap-2 bg-emerald-950/80 text-emerald-400 border border-emerald-800/60 text-[11px] font-bold px-3 py-1 rounded uppercase tracking-widest"><Briefcase size={13} /> Espace Pro</span>
          <h2 className="font-extrabold italic uppercase tracking-tight text-white text-3xl md:text-5xl leading-[0.95] mt-5">
            Garages, ateliers,<br /><span className="text-chrome">carrossiers.</span>
          </h2>
          <p className="text-slate-400 mt-5 max-w-md leading-relaxed">Tarifs négociés, remise personnalisée, facturation HT et un espace de commande dédié. Rejoignez les professionnels qui font confiance à KTSauto.</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/professionnels" data-testid="home-pro-cta-btn" className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold px-6 py-3.5 rounded-xl uppercase tracking-wide text-sm shadow-lg shadow-emerald-900/40 transition-colors">
              Ouvrir un compte pro <ArrowRight size={18} />
            </Link>
            <Link to="/professionnels/conditions" className="inline-flex items-center gap-2 text-slate-300 hover:text-white font-semibold px-5 py-3.5 rounded-xl border border-white/10 hover:border-white/30 transition-colors text-sm">
              Voir les conditions
            </Link>
          </div>
        </div>
        <div className="relative min-h-[260px] lg:min-h-0">
          <img src="https://images.unsplash.com/photo-1723099971299-3789db53604c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Atelier mécanique" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F0C] via-transparent to-transparent" />
          <div className="absolute bottom-6 right-6 bg-[#08090C]/80 backdrop-blur-md border border-white/10 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center text-white"><Wrench size={18} /></div>
            <div>
              <div className="text-white font-bold text-sm">Jusqu'à -25 %</div>
              <div className="text-slate-400 text-[12px]">sur tout le catalogue</div>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);


export const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    api.get("/reviews/latest", { params: { limit: 3 } }).then(({ data }) => { if (Array.isArray(data)) setReviews(data); }).catch(() => {});
    api.get("/reviews/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);
  if (reviews.length === 0) return null;
  return (
  <section data-testid="home-testimonials" className="max-w-[1440px] mx-auto px-5 mt-24">
    <Reveal>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B3B] font-bold mb-3">Ils roulent avec KTSauto</div>
          <h2 className="speed-line font-extrabold italic uppercase tracking-tight text-slate-50 text-3xl md:text-4xl">Avis clients</h2>
        </div>
        <div className="hidden sm:flex items-center gap-1 text-[#f5a623]">
          {[...Array(5)].map((_, i) => <Star key={i} size={18} className="fill-[#f5a623]" />)}
          <span className="text-slate-400 text-sm ml-2" data-testid="home-reviews-average">{summary?.count ? `${String(summary.average).replace(".", ",")} / 5 · ${summary.count} avis` : ""}</span>
        </div>
      </div>
    </Reveal>
    <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {reviews.map((r) => (
        <Item key={r.id} className="relative bg-gradient-to-b from-[#141721] to-[#0A0C10] border border-[#1E232D] rounded-2xl p-7 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <Quote size={30} className="text-[#E01F1F]/40" />
            <div className="flex items-center gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= r.rating ? "fill-[#f5a623] text-[#f5a623]" : "text-slate-700"} />)}</div>
          </div>
          <p className="text-slate-200 leading-relaxed text-[15px] flex-1 line-clamp-5">“{r.comment}”</p>
          <div className="mt-6 pt-5 border-t border-white/5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#E01F1F]/15 border border-[#E01F1F]/30 flex items-center justify-center font-display font-bold text-[#FF3B3B]">{(r.user_name || "C")[0].toUpperCase()}</div>
            <div className="min-w-0">
              <div className="text-white font-bold text-sm">{r.user_name}</div>
              <div className="text-slate-500 text-[12px] truncate">{r.product_name}</div>
            </div>
          </div>
        </Item>
      ))}
    </Stagger>
  </section>
  );
};
